import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import PlayerForm, { SubmitPlayerForm } from 'modules/players/ui/components/player-form';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { useCreateTeamPlayer } from 'modules/teams/ui/hooks/use-create-team-player';
import Modal from 'ud-ui/components/modal';

interface CreatePlayerModalProps {
  tournamentId: number;
  teamId: number;
  /** Состав команды: по нему до похода на бэкенд ловится дубль. */
  players: TeamPlayer[];
  /** Игрок создан и добавлен в команду — самое время перечитать заявки. */
  onCreated: () => void;
  onClose: () => void;
}

/**
 * Добавление игрока в команду прямо из заявок, второй флоу (v2).
 *
 * Форма здесь **та же самая**, что на экране `teams/:id/players/create` карточки
 * команды (`PlayerForm` + `useCreateTeamPlayer`), и делает то же самое: заводит игрока
 * в организации, добавляет его в команду и дорисовывает заявку в статусе «Ожидает
 * решения». Раньше в заявках жила своя укороченная форма из четырёх полей поверх
 * списка (`create-team-player-form`) — ни фото, ни роста с весом в ней не было, и
 * заведённого так игрока приходилось дозаполнять во второй раз в другом разделе.
 *
 * Окно монтируется только на время показа: закрытие сбрасывает поля формы, и
 * повторное открытие начинается с чистого листа.
 */
const CreatePlayerModal = ({ tournamentId, teamId, players, onCreated, onClose }: CreatePlayerModalProps) => {
  const { t } = useTranslation();

  const { createPlayer, isLoading } = useCreateTeamPlayer({ tournamentId, teamId, players });

  const handleSubmit = useCallback<SubmitPlayerForm>(
    async ({ values, formikHelpers }) => {
      let created = false;

      try {
        created = await createPlayer(values);
      } finally {
        formikHelpers.setSubmitting(false);
      }

      // Отказ оставляет окно открытым с введёнными значениями: пользователь поправит
      // данные и отправит снова, а не будет набирать форму заново.
      if (created) {
        onCreated();
        onClose();
      }
    },
    [createPlayer, onClose, onCreated],
  );

  return (
    <Modal show onClose={onClose} size="lg">
      <Modal.Header
        title={t('applications.v2.team.addPlayerModal.title')}
        subtitle={t('applications.v2.team.addPlayerModal.subtitle')}
      />
      <Modal.Body>
        <PlayerForm
          onSubmit={handleSubmit}
          isLoading={isLoading}
          onCancel={onClose}
          submitLabel={t('applications.v2.team.addPlayerModal.submit')}
        />
      </Modal.Body>
    </Modal>
  );
};

export default CreatePlayerModal;
